import React from 'react';
import Price from '../../../../../../../../Foundation/Common/code/Views/Foundation/Common/Price.jsx';
import ProductFavoriteButton from '../../../../../../../../Foundation/React/code/Components/ProductFavoriteButton.jsx';
import { ProductSliderItem } from './ProductSliderItem.ts';
import { ProductSliderOptions } from './ProductSliderOptions.ts';

type ProductSliderCardProps = {
  product: ProductSliderItem;
  position: number;
  options: ProductSliderOptions;
  favoriteIds?: string[];
};

const ProductSliderCard = ({ product, position, options, favoriteIds = [] }: ProductSliderCardProps) => {
  const badge = product.badges?.find((b) => b.categoryId !== 'Features');
  const priceContainer = product.priceContainer;
  const titlePrefix = product.numberOfItems && product.numberOfItems > 1 ? `${product.numberOfItems} x ` : '';

  const image = product.imageUrl ? (
    <img
      src={product.imageUrl}
      alt={product.imageAlt ?? product.webTitle1}
      width={400}
      height={300}
      className="lozad"
      loading="lazy"
    />
  ) : null;

  const imageBlock = product.url ? (
    <a href={product.url}>
      {image}
      {badge && options.showBadges && (
        <div className="productSliderItem__badge" data-product-badge-id={product.productId}>
          <span className="badge">{badge.text}</span>
        </div>
      )}
    </a>
  ) : (
    image
  );

  const detailsContent = (
    <>
      <div className="heading6 mt-5">
        {titlePrefix}
        {product.webTitle1}
      </div>
      {product.webTitle2 && <div className="paragraph--s description text--truncate-3">{product.webTitle2}</div>}
      {priceContainer && (
        <div className="price mt-3">
          <Price price={priceContainer} />
        </div>
      )}
    </>
  );

  const details = product.url ? <a href={product.url}>{detailsContent}</a> : detailsContent;

  return (
    <div
      className="productSliderItem"
      data-erpoutdoor={product.erpOutdoor}
      data-productstatus={product.statusErp}
      data-productname={product.webTitle1}
      data-productid={product.productId}
    >
      <div
        data-productname={product.webTitle1}
        data-pimid={product.productId}
        className="productFavoriteButtonContainer"
      >
        <ProductFavoriteButton
          productId={product.productId}
          productName={product.webTitle1}
          favoriteIds={favoriteIds}
        />
      </div>
      <span
        className="d-none analytics-data"
        data-currency={product.priceContainer?.CurrencyCode}
        data-name={product.productName ?? product.webTitle1}
        data-price={product.priceContainer?.Price}
        data-pimid={product.productId}
        data-position={position}
        data-listname={options.analyticsProductListName}
        data-sku={product.skuNumber}
      />
      <div className="tag-manager-product-slider-item">
        <div className="image-container">
          {imageBlock}
          {options.showAddToCartButton && product.addToCartButton && (
            <button
              className="productSliderItem__button accessory-add-to-cart tag-manager-add-to-cart-button"
              data-product-ids-for-solution={product.addToCartButton.ProductIdsForSolution ?? ''}
              data-erpoutdoor={product.erpOutdoor}
              data-product-id={product.productId}
              data-is-sellable={product.addToCartButton.IsSellable ? 'True' : 'False'}
              type="button"
            >
              <i className="le-icon-shopping-cart" aria-hidden="true" />
              <i className="le-icon-check" aria-hidden="true" />
              <i className="le-icon-vector spinner" aria-hidden="true" />
              <span className="sr-only">Add {product.webTitle1} to cart</span>
            </button>
          )}
          {options.showInfoButton && !options.showAddToCartButton && (
            <button
              className="productSliderItem__button productSliderItem__infoButton"
              data-productId={product.productId}
              data-canonical={product.canonical ?? ''}
              type="button"
            >
              <i className="le-icon-info" />
              <span className="sr-only">Open info modal</span>
            </button>
          )}
        </div>

        {options.isExtranet && product.erpOutdoor && (
          <p className="paragraph--s mt-0 mb-0 d-lg-block text-color--gray60">{product.erpOutdoor}</p>
        )}

        {details}
      </div>
    </div>
  );
};

export default ProductSliderCard;
